import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Copy, Upload, CheckCircle2, AlertCircle, Phone,
  Landmark, Wallet, Loader2, Image as ImageIcon, Send
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { uploadImageToBunnyDirect } from '../utils/bunnyUpload';

const PAYMENT_METHODS = [
  {
    id: 'kuraimi',
    name: 'الكريمي (Al-Kuraimi)',
    icon: Landmark,
    details: [
      { label: 'الاسم', value: 'أصيل صلاح محمد أحمد' },
      { label: 'حساب ريال يمني', value: '3010953099' },
      { label: 'حساب دولار أمريكي', value: '3018861077' },
      { label: 'حساب ريال سعودي', value: '3025543784' }
    ]
  },
  {
    id: 'jeeb',
    name: 'محفظة جيب (Jeeb)',
    icon: Wallet,
    details: [
      { label: 'رقم الهاتف (1)', value: '775412642' },
      { label: 'رقم الهاتف (2)', value: '783338339' }
    ]
  },
  {
    id: 'jawali',
    name: 'جوالي (Jawali)',
    icon: Wallet,
    details: [
      { label: 'رقم الهاتف (1)', value: '775412642' },
      { label: 'رقم الهاتف (2)', value: '783338339' }
    ]
  },
  {
    id: 'floosak',
    name: 'فلوسك (Floosak)',
    icon: Wallet,
    details: [
      { label: 'رقم الهاتف (1)', value: '775412642' },
      { label: 'رقم الهاتف (2)', value: '783338339' }
    ]
  },
  {
    id: 'onecash',
    name: 'ون كاش (OneCash)',
    icon: Wallet,
    details: [
      { label: 'رقم الهاتف (1)', value: '775412642' },
      { label: 'رقم الهاتف (2)', value: '783338339' }
    ]
  }
];

export default function PaymentModal({ isOpen, onClose, course, currentUser, onSuccess }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('تم نسخ رقم الحساب بنجاح');
    } catch (err) {
      showToast('حدث خطأ أثناء النسخ');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('الرجاء اختيار صورة صالحة.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('حجم الصورة يجب أن لا يتجاوز 5 ميجابايت.');
        return;
      }
      setError('');
      setReceiptImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMethod) return setError('الرجاء اختيار طريقة الدفع.');
    if (!referenceNumber.trim()) return setError('الرجاء إدخال رقم العملية / المرجع.');
    if (!receiptImage) return setError('الرجاء إرفاق صورة السند.');

    setLoading(true);
    setError('');

    try {
      // Upload image to BunnyCDN directly
      const imageUrl = await uploadImageToBunnyDirect(receiptImage, 'receipts');

      const payload = {
        studentId: currentUser.uid,
        studentName: currentUser.displayName || 'بدون اسم',
        studentEmail: currentUser.email || '',
        courseId: course.id,
        courseName: course.title,
        status: 'pending',
        requestedAt: serverTimestamp(),
        paymentMethod: PAYMENT_METHODS.find(m => m.id === selectedMethod)?.name || selectedMethod,
        referenceNumber: referenceNumber.trim(),
        receiptUrl: imageUrl,
      };

      await addDoc(collection(db, 'course_registrations'), payload);
      setSubmitSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error submitting payment:', err);
      setError('حدث خطأ أثناء إرسال الطلب. حاول مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppConfirm = () => {
    const phone = '967783338339'; // Target WhatsApp Number
    const methodStr = PAYMENT_METHODS.find(m => m.id === selectedMethod)?.name || '';
    const msg = `اهلا فريق لباب قمت بطلب التسجيل في دورة: ${course?.title || ''}. طريقة الدفع: ${methodStr}. رقم المرجع: ${referenceNumber}.`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
          onClick={loading || submitSuccess ? undefined : onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl bg-white dark:bg-zinc-900 rounded-[2rem] shadow-2xl overflow-hidden border border-white/10 flex flex-col max-h-[90vh]"
          dir="rtl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 sm:px-8 border-b border-slate-100 dark:border-white/5 shrink-0">
            <div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white">بوابة الدفع اليدوي</h2>
              <p className="text-sm font-medium text-slate-500 dark:text-zinc-400 mt-1">
                التسجيل في: <span className="font-bold text-primary-600 dark:text-primary-400">{course?.title}</span>
              </p>
            </div>
            {!loading && !submitSuccess && (
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:px-8 space-y-8">
            {submitSuccess ? (
              <div className="flex flex-col items-center justify-center py-10 text-center space-y-6 animate-in slide-in-from-bottom-6 duration-500">
                <div className="relative">
                  <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full scale-150"></div>
                  <div className="w-24 h-24 bg-emerald-100 dark:bg-emerald-500/20 rounded-[2rem] flex items-center justify-center relative z-10 border border-emerald-200 dark:border-emerald-500/30 shadow-xl">
                    <CheckCircle2 className="w-12 h-12 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white">تم إرسال طلبك بنجاح!</h3>
                  <p className="text-slate-500 dark:text-zinc-400 max-w-sm mx-auto leading-relaxed font-medium">
                    سيتم مراجعة الدفع وتفعيل اشتراكك في الدورة مباشرة وستصلك رسالة تأكيد.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4 w-full justify-center">
                  <button
                    onClick={handleWhatsAppConfirm}
                    className="px-8 py-3.5 bg-[#25D366] hover:bg-[#128C7E] text-white font-black rounded-2xl transition-all shadow-lg shadow-[#25D366]/30 flex items-center justify-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    تأكيد عبر واتساب
                  </button>
                  <button
                    onClick={onClose}
                    className="px-8 py-3.5 bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-700 dark:text-white font-bold rounded-2xl transition-colors flex items-center justify-center gap-2"
                  >
                    إغلاق النافذة
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                  <div className="p-4 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 rounded-2xl flex items-center gap-3 text-rose-600 dark:text-rose-400">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <p className="font-bold text-sm">{error}</p>
                  </div>
                )}

                {/* 1. Select Payment Method */}
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                    <span className="w-6 h-6 rounded-md bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 flex items-center justify-center text-sm">1</span>
                    اختر طريقة الدفع
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {PAYMENT_METHODS.map((method) => {
                      const Icon = method.icon;
                      const isSelected = selectedMethod === method.id;
                      return (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => setSelectedMethod(method.id)}
                          className={`flex items-center gap-3 p-4 rounded-2xl border transition-all duration-300 text-right ${isSelected
                              ? 'bg-primary-50 dark:bg-primary-500/10 border-primary-500 shadow-md shadow-primary-500/10 ring-2 ring-primary-500/20'
                              : 'bg-white dark:bg-zinc-800/50 border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'
                            }`}
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-600' : 'bg-slate-100 dark:bg-zinc-700 text-slate-500'}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className={`font-bold text-sm ${isSelected ? 'text-primary-700 dark:text-primary-400' : 'text-slate-700 dark:text-zinc-300'}`}>
                            {method.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 2. Transfer Data */}
                <AnimatePresence mode="wait">
                  {selectedMethod && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-slate-50 border border-slate-200 dark:bg-zinc-800/50 dark:border-white/5 rounded-3xl p-5 space-y-4 shadow-inner">
                        <h4 className="font-bold text-slate-700 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2 mb-4">
                          <Landmark className="w-4 h-4 text-primary-500" />
                          بيانات الحساب للتحويل
                        </h4>

                        <div className="grid sm:grid-cols-2 gap-3">
                          {PAYMENT_METHODS.find(m => m.id === selectedMethod)?.details.map((detail, idx) => (
                            <div key={idx} className="flex flex-col bg-white dark:bg-zinc-900 border border-slate-100 dark:border-white/10 rounded-2xl p-4 gap-2">
                              <span className="text-xs font-bold text-slate-400 dark:text-zinc-500">{detail.label}</span>
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-black text-slate-900 dark:text-white text-base tracking-wide" dir="ltr">{detail.value}</span>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(detail.value)}
                                  className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-primary-50 dark:bg-zinc-800 dark:hover:bg-primary-500/20 text-slate-500 hover:text-primary-600 dark:text-zinc-400 dark:hover:text-primary-400 flex items-center justify-center transition-colors"
                                  title="نسخ"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* 3. Form Data */}
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                    <span className="w-6 h-6 rounded-md bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 flex items-center justify-center text-sm">2</span>
                    تأكيد التحويل
                  </h3>

                  <div className="grid sm:grid-cols-2 gap-6">
                    {/* Reference Number */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 dark:text-zinc-300">رقم العملية / المرجع</label>
                      <input
                        type="text"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        placeholder="أدخل رقم عملية التحويل..."
                        className="w-full px-5 py-4 bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white font-bold outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all placeholder:text-slate-400 placeholder:font-medium"
                      />
                    </div>

                    {/* Receipt Upload */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 dark:text-zinc-300">صورة السند (أو لقطة شاشة)</label>
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full relative h-[60px] bg-slate-50 dark:bg-zinc-800/50 border border-dashed border-slate-300 dark:border-white/20 rounded-2xl flex items-center justify-center cursor-pointer hover:bg-slate-100 dark:hover:bg-zinc-800 hover:border-primary-400 dark:hover:border-primary-500 transition-colors overflow-hidden group"
                      >
                        {receiptPreview ? (
                          <div className="absolute inset-0 flex items-center px-4 justify-between bg-primary-50 dark:bg-primary-500/10 border-solid border-primary-200 dark:border-primary-500/30 border-2 rounded-2xl">
                            <span className="font-bold text-sm text-primary-700 dark:text-primary-400 truncate">تم إرفاق صورة السند</span>
                            <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 shadow-sm border border-black/10">
                              <img src={receiptPreview} className="w-full h-full object-cover" alt="Preview" />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors font-bold text-sm">
                            <Upload className="w-5 h-5" />
                            <span className="truncate">اختر ملف الصورة...</span>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                </div>

              </form>
            )}
          </div>

          {/* Footer Actions */}
          {!submitSuccess && (
            <div className="p-6 sm:px-8 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-zinc-900/50 flex gap-4 shrink-0">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 py-4 bg-primary-600 hover:bg-primary-500 disabled:bg-slate-300 disabled:dark:bg-zinc-700 text-white font-black rounded-2xl transition-all shadow-lg shadow-primary-600/20 flex items-center justify-center gap-2 text-base"
              >
                {loading ? (
                  <><Loader2 className="w-6 h-6 animate-spin" /> جاري الإرسال...</>
                ) : (
                  <>إرسال طلب التسجيل للمراجعة</>
                )}
              </button>
            </div>
          )}
        </motion.div>

        {/* Global Toast within Modal context */}
        <AnimatePresence>
          {toastMessage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-xl font-bold text-sm z-[110] flex items-center gap-2"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              {toastMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AnimatePresence>
  );
}
